import React, { useMemo } from "react";
import { Box, Stack, Typography, Chip, Button } from "@mui/material";
import { Play, CheckCircle2, ArrowLeft } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useNavigate, useParams } from "react-router-dom";
import { ticketService } from "../../api/services/ticketService";
import { slaService } from "../../api/services/slaService";
import { planService } from "../../api/services/planService";
import { identityService } from "../../api/services/identityService";
import { useAuth } from "../../contexts/AuthContext";
import { PageHeader, Loader } from "../../components/Common";
import KanbanBoard from "../../components/KanbanBoard";

export default function TicketPlanBoard() {
  const { projectId, planId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("admin.plans.manage");

  const { data: plan, isLoading: planLoading } = useQuery({ queryKey: ["plan", planId], queryFn: () => planService.get(planId) });
  const { data: statuses } = useQuery({ queryKey: ["statuses"], queryFn: slaService.listStatuses, staleTime: Infinity });
  const { data: priorities } = useQuery({ queryKey: ["priorities"], queryFn: slaService.listPriorities, staleTime: Infinity });
  const { data: transitions } = useQuery({ queryKey: ["workflow-transitions"], queryFn: slaService.listTransitions, staleTime: Infinity });
  const { data: employees } = useQuery({ queryKey: ["employees"], queryFn: identityService.listEmployees });

  const { data: tickets, isLoading: ticketsLoading } = useQuery({
    queryKey: ["tickets-board", { plan_id: planId }],
    queryFn: () => ticketService.listBoard({ plan_id: planId }),
  });

  const priorityById = useMemo(() => Object.fromEntries((priorities || []).map((p) => [p.id, p])), [priorities]);
  const employeeById = useMemo(() => Object.fromEntries((employees || []).map((e) => [e.id, e])), [employees]);
  const sortedStatuses = useMemo(() => [...(statuses || [])].sort((a, b) => a.sort_order - b.sort_order), [statuses]);

  const cardsByColumn = useMemo(() => {
    const map = {};
    for (const s of sortedStatuses) map[s.id] = [];
    for (const t of tickets || []) {
      if (!map[t.status_id]) map[t.status_id] = [];
      map[t.status_id].push(t);
    }
    return map;
  }, [tickets, sortedStatuses]);

  const allowedPairs = useMemo(() => {
    const set = new Set();
    for (const t of transitions || []) set.add(`${t.from_status_id}-${t.to_status_id}`);
    return set;
  }, [transitions]);

  const isDropAllowed = (fromId, toId) => {
    if (!transitions || transitions.length === 0) return true;
    return allowedPairs.has(`${fromId}-${toId}`);
  };

  const invalidateBoard = () => queryClient.invalidateQueries({ queryKey: ["tickets-board", { plan_id: planId }] });

  const moveMutation = useMutation({
    mutationFn: ({ ticketId, statusId }) => ticketService.update(ticketId, { status_id: statusId }),
    onSuccess: invalidateBoard,
    onError: (err) => {
      enqueueSnackbar(err.response?.data?.detail || "Couldn't move that ticket", { variant: "error" });
      invalidateBoard();
    },
  });

  const startMutation = useMutation({
    mutationFn: () => planService.start(planId),
    onSuccess: () => { enqueueSnackbar("Phase started", { variant: "success" }); queryClient.invalidateQueries({ queryKey: ["plan", planId] }); },
    onError: (err) => enqueueSnackbar(err.response?.data?.detail || "Couldn't start plan", { variant: "error" }),
  });

  const completeMutation = useMutation({
    mutationFn: () => planService.complete(planId),
    onSuccess: () => { enqueueSnackbar("Phase completed", { variant: "success" }); queryClient.invalidateQueries({ queryKey: ["plan", planId] }); },
    onError: (err) => enqueueSnackbar(err.response?.data?.detail || "Couldn't complete plan", { variant: "error" }),
  });

  const renderCard = (ticket) => {
    const priority = priorityById[ticket.priority_id];
    const assignee = employeeById[ticket.assignee_employee_id];
    return (
      <Box onClick={() => navigate(`/tickets/${ticket.id}`)}>
        <Typography variant="caption" color="text.secondary">{ticket.ticket_number}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>{ticket.subject}</Typography>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {priority ? (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: priority.color }} />
              <Typography variant="caption">{priority.name}</Typography>
            </Stack>
          ) : <span />}
          <Typography variant="caption" color="text.secondary">{assignee ? assignee.name : "Unassigned"}</Typography>
        </Stack>
      </Box>
    );
  };

  if (planLoading) return <Loader />;

  return (
    <Box>
      <Button
        size="small" startIcon={<ArrowLeft size={14} />} sx={{ mb: 1 }}
        onClick={() => navigate(`/tickets/p/${projectId}/backlog`)}
      >
        Back to Backlog
      </Button>
      <PageHeader
        title={plan ? plan.name : "Phase Board"}
        subtitle={plan ? `${plan.start_date} → ${plan.end_date}` : ""}
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            {plan && <Chip size="small" label={plan.status.replace("_", " ")} />}
            {canManage && plan?.status === "not_started" && (
              <Button size="small" variant="contained" startIcon={<Play size={14} />} onClick={() => startMutation.mutate()}>Start</Button>
            )}
            {canManage && plan?.status === "active" && (
              <Button size="small" variant="outlined" startIcon={<CheckCircle2 size={14} />} onClick={() => completeMutation.mutate()}>Complete</Button>
            )}
          </Stack>
        }
      />

      <KanbanBoard
        columns={sortedStatuses}
        cardsByColumn={cardsByColumn}
        loading={ticketsLoading}
        renderCard={renderCard}
        isDropAllowed={isDropAllowed}
        onCardDrop={(ticketId, _fromStatusId, toStatusId) => moveMutation.mutate({ ticketId, statusId: toStatusId })}
      />
    </Box>
  );
}
